import gql from "graphql-tag";

import {type GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";

import type {
    GetAvatars,
    GetAvatars_user as GqlUser,
    GetAvatars_user_allAvatars as UserAvatar,
} from "./__graphql-types__/GetAvatars.ts";
import {
    getRequirementsForModel,
    getRequirementsForPart,
} from "./requirements-strings.ts";
import type {AvatarModel, AvatarPart} from "./types.ts";

const GET_AVATARS: GqlOperation<GetAvatars> = gqlOp(gql`
    query GetAvatars {
        user {
            id
            avatar {
                name
            }
            allAvatars {
                avatar {
                    category
                    imageSrc
                    isDefaultForCategory
                    name
                }
                isAvailable
            }
            hasChangedAvatar
        }
    }
`);

export type TransformedAvatarsData = {
    avatarModels: Array<AvatarModel>;
    selectedModel: string;
    avatarParts: Array<AvatarPart>;
    selectedPart: string;
    hasChangedAvatar: boolean;
};

export const fetchAvatars = (
    gqlFetch: GqlFetchFn,
): Promise<TransformedAvatarsData | null | undefined> => {
    // We always fetch new data because `isAvailable` could change.
    return gqlFetch(GET_AVATARS).then((data: GetAvatars) => {
        const user = data.user;
        if (!user) {
            return null;
        }

        return mapGqlAvatars(user);
    });
};

export const mapGqlAvatars = (user: GqlUser): TransformedAvatarsData => {
    let selectedModel = "";
    const selectedPart = user.avatar.name;
    const incompleteAvatarModels: Array<AvatarModel> = [];
    const avatarParts: Array<AvatarPart> = [];

    const allAvatars: Array<UserAvatar | null | undefined> =
        user?.allAvatars ?? [];
    const modelAvailability: Record<string, boolean> = {};

    for (const userAvatar of allAvatars) {
        if (!userAvatar) {
            continue;
        }
        const avatar = userAvatar.avatar;

        // Category (aka. model) availability defaults to false.
        // Default to FALSE.
        const category = avatar.category;
        if (!modelAvailability.hasOwnProperty(category)) {
            modelAvailability[category] = false;
        }
        // If any avatar in this category is available,
        // make the category available.
        if (modelAvailability[category] === false) {
            modelAvailability[category] = userAvatar.isAvailable;
        }

        const part: AvatarPart = {
            imageSrc: avatar.imageSrc,
            requirements: getRequirementsForPart(avatar.name),
            isAvailable: userAvatar.isAvailable,
            partType: "style",
            name: avatar.name,
            forModel: avatar.category,
        };
        avatarParts.push(part);

        if (part.name === selectedPart) {
            selectedModel = avatar.category;
        }

        if (avatar.isDefaultForCategory) {
            const model: AvatarModel = {
                imageSrc: avatar.imageSrc,
                requirements: getRequirementsForModel(avatar.category),
                isAvailable: userAvatar.isAvailable,
                partType: "model",
                name: avatar.category,
            };
            incompleteAvatarModels.push(model);
        }
    }

    const avatarModels = incompleteAvatarModels.map((model) => {
        model.isAvailable = modelAvailability[model.name];
        return model;
    });

    return {
        avatarModels,
        selectedModel,
        avatarParts,
        selectedPart,
        hasChangedAvatar: user.hasChangedAvatar,
    };
};
